import { type TripTypeListItem, type TripTypeListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { TripTypeFormDrawer } from '../components/TripTypeFormDrawer';
import { useTripTypesList } from '../use-trip-types';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

/**
 * Trip Type master CRUD. Add + edit are exposed; delete is deliberately
 * NOT - a trip type is referenced by the `destination_trip_types` join,
 * so dropping a row would violate the relation on every destination that
 * carries it. Operators turn unused trip types `Active: off` via the
 * edit form to hide them from the lead / user filters without breaking
 * the relation.
 */
export const TripTypesListPage = (): React.JSX.Element => {
  // Deletion intentionally not exposed - see the docblock above.

  const columns: ListScreenColumns<TripTypeListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<TripTypeListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-600">{row.description ?? '-'}</span>,
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<TripTypeListItem, TripTypeListQuery>
      title="Trip Types"
      description="Trip categories a destination can advertise (FIT / Group / Cruise / operator-added). Surfaces in the lead-form filter and the user-form destination picker."
      useList={useTripTypesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add trip type' }}
      renderDrawer={({ open, mode, row, close }) => (
        <TripTypeFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          tripType={row}
        />
      )}
      // Deletion is intentionally not exposed - a trip type is
      // referenced by the `destination_trip_types` join. Operators
      // deactivate via the edit form (Active: off) instead.
      rowActions={{
        edit: true,
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No trip types match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load trip types',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
